'use client';

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence, useInView, PanInfo } from 'framer-motion';
import { useNavbar } from '@/components/providers/NavbarProvider';
import ProjectCarousel3D from './ui/ProjectCarousel3D';
import MobileLiveProjectsShowcase from './MobileLiveProjectsShowcase';
import Icon from '@/components/ui/AppIcon';

// --- DATA ---
// --- DATA (Commented out as requested) ---
/*
interface Project {
    id: string;
    title: string;
    category: string;
    description: string;
    client: string;
    duration: string;
    tools: string;
    image: string;
    video?: string;
    link?: string;
}

const PROJECTS: Project[] = [
    {
        id: 'ak47',
        title: 'AK47 Sports',
        category: 'E-Commerce',
        description:
            'A premium cricket equipment store featuring a dynamic product showcase, seamless checkout, and a bold design system.',
        client: 'AK47 Sports Inc.',
        duration: '8 Weeks',
        tools: 'Next.js, Shopify',
        image: '/ak47_sports_project.jpg',
        link: '#',
        video: '/videos/ak47_showcase.mp4',
    },
    {
        id: 'pincode',
        title: 'Pincode Hotels',
        category: 'Hospitality Platform',
        description:
            'An intuitive booking platform designed for seamless user experiences. Features real-time availability and immersive tours.',
        client: 'Pincode Group',
        duration: '12 Weeks',
        tools: 'React, Node.js',
        image: '/pincode_app_project.jpg',
        link: '#',
        video: '/videos/pincode_hotels.mp4',
    },
    {
        id: 'pos',
        title: 'Point Of Sale',
        category: 'Retail System',
        description:
            'A streamlined POS interface built for speed and reliability. Handles complex transactions, loyalty programs, and offline modes seamlessly.',
        client: 'RetailChain Inc.',
        duration: '10 Weeks',
        tools: 'React, Electron',
        image:
            'https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
        link: '#',
    },
    {
        id: 'inventory',
        title: 'Inventory Management',
        category: 'Logistics Dashboard',
        description:
            'Real-time stock tracking system with automated reordering and predictive analytics to optimize supply chain efficiency.',
        client: 'LogiTech Solutions',
        duration: '14 Weeks',
        tools: 'Next.js, PostgreSQL',
        image:
            'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
        link: '#',
    },
    {
        id: 'admin',
        title: 'Admin Panel',
        category: 'Enterprise Control',
        description:
            'A centralized command center for managing users, content, and analytics. Features granular role-based access control and detailed reporting.',
        client: 'SaaS Corp',
        duration: 'Ongoing',
        tools: 'React, GraphQL',
        image:
            'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
        link: '#',
    },
];

const MotionImage = motion(Image);

// Default Configuration from User Snippet
const CONFIG = {
    stackOffset: 10, // px
    stackRotation: 5, // degrees (positive or negative depending on fan direction desired, snippet default was -10, let's try 5 for a clean fan)
    dragThreshold: 80,
    shadowIntensity: 0.2
};
*/

const LiveProjectsShowcase = () => {
    const containerRef = useRef<HTMLElement | null>(null);
    const { setIsVisible } = useNavbar();
    // const [videoUrl, setVideoUrl] = useState<string | null>(null);
    // const [expandedId, setExpandedId] = useState<string | null>(null);

    // Initialize Order: [0, 1, 2, 3, 4]
    // const [cardOrder, setCardOrder] = useState(() => PROJECTS.map((_, i) => i));

    // Navbar Logic
    const isInView = useInView(containerRef, { amount: 0.25 });
    useEffect(() => {
        setIsVisible(!isInView);
        return () => setIsVisible(true);
    }, [isInView, setIsVisible]);

    // Modal, Logic, Transforms removed as requested.
    /*
    useEffect(() => {
        if (!videoUrl) return;
        const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setVideoUrl(null);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [videoUrl]);
    const closeModal = useCallback(() => setVideoUrl(null), []);

    // --- LOGIC PORTED FROM SNIPPET ---
    const getCardTransform = useCallback((cardIndex: number) => {
        const stackPosition = cardOrder.indexOf(cardIndex);
        const positionFromBottom = cardOrder.length - 1 - stackPosition;

        // Snippet Logic:
        // zIndex: stackPosition
        // y: -positionFromBottom * stackOffset
        // rotate: positionFromBottom * stackRotation !!! (This ensures fanning)
        // scale: 1 - positionFromBottom * 0.02

        return {
            zIndex: stackPosition,
            y: -positionFromBottom * CONFIG.stackOffset,
            rotate: positionFromBottom * CONFIG.stackRotation, // This creates the fan
            scale: 1 - (positionFromBottom * 0.05), // Slightly more aggression on scale for visual depth
            opacity: 1,
        };
    }, [cardOrder]);

    const handleDragEnd = useCallback((event: any, info: PanInfo, cardIndex: number) => {
        const dragDistance = Math.abs(info.offset.x) + Math.abs(info.offset.y);
        const velocity = Math.abs(info.velocity.x) + Math.abs(info.velocity.y);

        if (dragDistance > CONFIG.dragThreshold || velocity > 800) {
            // Reorder: Move dragged card (Top) to Bottom (Start of array)
            setCardOrder((prevOrder) => {
                const newOrder = [...prevOrder];
                const draggedCardPosition = newOrder.indexOf(cardIndex);
                // In user snippet: const draggedCard = newOrder.splice(draggedCardPosition, 1)[0];
                // newOrder.unshift(draggedCard);
                // This assumes draggedCardPosition is the last one?
                // The snippet enables drag only for top card.

                if (draggedCardPosition > -1) {
                    const [draggedCard] = newOrder.splice(draggedCardPosition, 1);
                    newOrder.unshift(draggedCard);
                }
                return newOrder;
            });
        }
    }, []);

    // Variants from Snippet
    const cardVariants = {
        initial: (custom: any) => ({
            x: 0,
            y: custom.y,
            rotate: custom.rotate,
            scale: custom.scale,
            zIndex: custom.zIndex,
            opacity: custom.opacity
        }),
        animate: (custom: any) => ({
            x: 0,
            y: custom.y,
            rotate: custom.rotate,
            scale: custom.scale,
            zIndex: custom.zIndex,
            opacity: custom.opacity,
            transition: {
                type: "spring",
                damping: 30,
                stiffness: 500,
                mass: 0.5,
                restDelta: 0.01,
                restSpeed: 0.01
            }
        }),
        drag: {
            scale: 1.05,
            rotate: 0,
            transition: { duration: 0.05 }
        }
    };
    */

    return (
        <section id="ecosystem-showcase" ref={containerRef} className="py-12 md:py-24 relative min-h-screen flex flex-col items-center overflow-hidden bg-[#F8F9FA]">
            {/* AMBIENT STUDIO LIGHTING */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-white rounded-full blur-[120px] opacity-60" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1200px] h-[500px] bg-slate-200/40 rounded-full blur-[100px]" />

                {/* NEW: DYNAMIC DATA GRID BACKGROUND */}
                <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

                {/* NEW: GRID RUNNERS (Bidirectional Data Flow) */}
                <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                    {/* We generate a static set of runners to avoid hydration mismatches, 
                        normally one would use a seeded random or useEffect, but for specific visual 'chaos' 
                        we can map a fixed array with deterministic math or just use client-side only rendering. 
                        Here we use a fixed count and rely on CSS/Framer for the motion. 
                    */}
                    {/* VERTICAL RUNNERS (Blue Theme) */}
                    {[...Array(8)].map((_, i) => {
                        const isDown = i % 2 === 0;
                        const duration = 3 + (i % 5);
                        const delay = i * 0.5;

                        return (
                            <motion.div
                                key={`v-${i}`}
                                className="absolute w-[1px] h-[150px] bg-gradient-to-b from-transparent via-blue-500 to-transparent opacity-40"
                                style={{
                                    left: `calc(50% + ${(i - 4) * 120}px)`, // increased spread
                                }}
                                initial={{ y: isDown ? '-100%' : '100%', opacity: 0 }}
                                animate={{
                                    y: isDown ? '120vh' : '-120vh',
                                    opacity: [0, 1, 1, 0]
                                }}
                                transition={{
                                    duration: duration,
                                    repeat: Infinity,
                                    ease: "linear",
                                    delay: delay,
                                    repeatDelay: Math.random() * 5
                                }}
                            />
                        );
                    })}

                    {/* HORIZONTAL RUNNERS (Blue Theme) */}
                    {[...Array(8)].map((_, i) => {
                        const isRight = i % 2 === 0;
                        const duration = 5 + (i % 5);
                        const delay = i * 0.6;

                        return (
                            <motion.div
                                key={`h-${i}`}
                                className="absolute h-[1px] w-[150px] bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-40"
                                style={{
                                    top: `calc(50% + ${(i - 4) * 80}px)`, // increased spread
                                }}
                                initial={{ x: isRight ? '-100%' : '100%', opacity: 0 }}
                                animate={{
                                    x: isRight ? '100vw' : '-100vw',
                                    opacity: [0, 1, 1, 0]
                                }}
                                transition={{
                                    duration: duration,
                                    repeat: Infinity,
                                    ease: "linear",
                                    delay: delay,
                                    repeatDelay: Math.random() * 5
                                }}
                            />
                        );
                    })}
                </div>

                {/* NEW: FLOATING "STATUS" ELEMENTS */}
                <motion.div
                    animate={{ y: [0, -20, 0], rotate: [0, 5, 0], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-20 left-[10%] w-32 h-32 rounded-full border border-blue-100/30 bg-blue-50/5 blur-xl"
                />
                <motion.div
                    animate={{ y: [0, 30, 0], rotate: [0, -10, 0], opacity: [0.2, 0.5, 0.2] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute bottom-40 right-[10%] w-64 h-64 rounded-full border border-purple-100/30 bg-purple-50/5 blur-2xl"
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 w-full relative z-10 flex flex-col items-center">
                {/* EDITORIAL HEADER (Desktop Only - Mobile has its own in the component) */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="hidden lg:flex mb-24 text-center relative z-50 pointer-events-none flex-col items-center"
                >
                    <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-4 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse box-shadow-green" style={{ boxShadow: '0 0 10px #22c55e' }}></span>
                        Live Deployments
                    </span>
                    <h2 className="text-5xl md:text-7xl font-bold text-slate-900 tracking-tight leading-tight">
                        Ecosystems <span className="text-slate-300 font-light italic bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">in</span> Action.
                    </h2>
                </motion.div>

                {/* 3D CAROUSEL COMPONENT (Desktop) */}
                <div className="hidden lg:block w-full">
                    <ProjectCarousel3D
                        cardWidth={380}
                        cardHeight={500}
                        perspective={1200}
                        gap={80}
                    />
                </div>

                {/* MOBILE SWIPE DECK (Mobile) */}
                <div className="block lg:hidden w-full">
                    <MobileLiveProjectsShowcase />
                </div>
            </div>

            {/* VIDEO MODAL */}
            {/*
            <AnimatePresence>
                {videoUrl && (
                    <div
                        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
                        onMouseDown={closeModal}
                        role="dialog"
                        aria-modal="true"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="relative w-full max-w-6xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl border border-white/10"
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <video src={videoUrl} controls autoPlay className="w-full h-full object-contain" />
                            <button
                                type="button"
                                onClick={closeModal}
                                className="absolute top-6 right-6 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
                                aria-label="Close video"
                            >
                                <Icon name="XMarkIcon" />
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            */}
        </section>
    );
};

// --- CARD (CINEMATIC POSTER) ---
/*
const FramerCard = ({
    project,
    priorityImage,
    isActive,
    onToggle,
    onPlayVideo,
}: {
    project: Project;
    priorityImage?: boolean;
    isActive: boolean;
    onToggle: () => void;
    onPlayVideo: (url: string) => void;
}) => {
    const isRemoteImage = /^https?:\/\//.test(project.image);

    return (
        <div
            className="bg-white flex flex-col relative w-full h-full overflow-hidden group"
            style={{ borderRadius: 44 }}
            onClick={onToggle}
        >
            {/* IMAGE ZONE }
            <motion.div
                initial={false}
                animate={{ height: isActive ? 280 : '100%', borderRadius: isActive ? '0px 0px 44px 44px' : '0px' }}
                transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
                className="relative w-full z-0 overflow-hidden"
                style={{ borderRadius: 34 }} // Inner radius default
            >
                {/* Film Grain }
                <div className="absolute inset-0 opacity-20 pointer-events-none z-20 mix-blend-overlay" style={{ backgroundImage: 'url("https://framerusercontent.com/images/rR6HYXBrMmX4cRpXfXUOvpvpB0.png")' }}></div>

                <motion.div
                    className="absolute inset-0 z-0"
                    whileHover={!isActive ? { scale: 1.05 } : {}}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    {isRemoteImage ? (
                        <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                    ) : (
                        <Image
                            src={project.image}
                            alt={project.title}
                            fill
                            priority={!!priorityImage}
                            sizes="(max-width: 768px) 100vw, 500px"
                            className="object-cover"
                        />
                    )}
                </motion.div>

                {/* Gradient Overlay }
                <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10 transition-opacity duration-500 ${isActive ? 'opacity-90' : 'opacity-100'}`} />

                {/* TEXT OVERLAY }
                <div
                    className="absolute bottom-0 left-0 right-0 p-8 flex flex-col justify-end z-30"
                >
                    <div>
                        <motion.div className="flex items-center gap-3 mb-2 opacity-80">
                            <span className="px-2 py-0.5 rounded-full border border-white/30 bg-black/20 backdrop-blur-md text-[10px] font-bold tracking-widest text-white uppercase shadow-sm">
                                {project.category}
                            </span>
                        </motion.div>
                        <motion.h3 className="text-white font-bold text-3xl md:text-3xl leading-none tracking-tight">
                            {project.title}
                        </motion.h3>
                    </div>

                    {/* View Project Hint }
                    <AnimatePresence>
                        {!isActive && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileHover={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="absolute right-8 bottom-8"
                            >
                                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white">
                                    <Icon name="ArrowRightIcon" size={20} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* DETAILS BODY }
            <AnimatePresence>
                {isActive && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="flex-1 bg-white relative z-10 overflow-hidden flex flex-col pt-4"
                    >
                        {/* Close Button }
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggle(); }}
                            className="absolute top-4 right-6 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors z-20"
                        >
                            <Icon name="ChevronUpIcon" size={16} />
                        </button>

                        <div className="px-8 pb-8 flex flex-col gap-6 h-full">
                            <p className="text-slate-600 text-base leading-relaxed font-medium">
                                {project.description}
                            </p>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Client</span>
                                    <span className="text-slate-900 font-semibold">{project.client}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Services</span>
                                    <span className="text-slate-900 font-semibold truncate">{project.tools}</span>
                                </div>
                            </div>

                            <div className="mt-auto flex gap-3 pt-4">
                                { /* Actions... }
                                <a
                                    href={project.link || '#'}
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 flex items-center justify-center gap-2"
                                >
                                    Visit
                                    <Icon name="ArrowRightIcon" size={14} />
                                </a>
                                {project.video ? (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onPlayVideo(project.video!); }}
                                        className="flex-1 py-4 bg-slate-100 text-slate-900 rounded-2xl font-bold text-sm hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                                    >
                                        Watch
                                        <Icon name="PlayIcon" size={14} />
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const MetaPill = ({ value }: { value: string }) => (
    <div className="px-3 py-2 bg-white rounded-lg border border-slate-100/50 shadow-sm text-xs font-semibold text-slate-600">
        {value}
    </div>
);
*/

export default LiveProjectsShowcase;
